import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, Storage } from '../firebase';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminNeckLabels = () => {
  const [brandsWithNeckLabels, setBrandsWithNeckLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch all brands with neck labels enabled
  useEffect(() => {
    fetchBrandsWithNeckLabels();
  }, []);

  const fetchBrandsWithNeckLabels = async () => {
    setLoading(true);
    try {
      // Fetch all users with neckLabels enabled
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, where('neckLabels', '==', true));
      const usersSnapshot = await getDocs(usersQuery);

      const brandsData = [];

      // For each user, fetch their neck labels
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();

        // Fetch branding collection for this user
        const brandingRef = collection(db, 'users', userId, 'branding');
        const brandingSnapshot = await getDocs(brandingRef);

        // Filter only neck labels
        const neckLabels = brandingSnapshot.docs
          .map(doc => ({
            id: doc.id,
            userId: userId,
            ...doc.data()
          }))
          .filter(item => item.Neck_label);

        if (neckLabels.length > 0) {
          brandsData.push({
            userId: userId,
            brandName: userData.brandName || userData.email || 'Unknown Brand',
            email: userData.email || 'N/A',
            neckLabels: neckLabels
          });
        }
      }

      setBrandsWithNeckLabels(brandsData);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to load neck labels data");
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (label) => {
    setSelectedLabel(label);
    setShowDeleteModal(true);
  };

  // Delete neck label
// Delete neck label
const handleDeleteLabel = async () => {
  if (!selectedLabel) return;

  try {
    // Delete from Firestore
    const docRef = doc(db, 'users', selectedLabel.userId, 'branding', selectedLabel.id);
    await deleteDoc(docRef);

    // Delete from Storage
    try {
      const imageRef = ref(Storage, selectedLabel.Neck_label);
      await deleteObject(imageRef);
    } catch (storageError) {
      console.log("Storage deletion error:", storageError);
    }

    toast.success("Neck label deleted successfully!");
    setShowDeleteModal(false);
    setSelectedLabel(null);
    
    // Update state locally instead of refetching
    setBrandsWithNeckLabels(prevBrands => 
      prevBrands.map(brand => {
        if (brand.userId === selectedLabel.userId) {
          const updatedNeckLabels = brand.neckLabels.filter(label => label.id !== selectedLabel.id);
          return {
            ...brand,
            neckLabels: updatedNeckLabels
          };
        }
        return brand;
      }).filter(brand => brand.neckLabels.length > 0) // Remove brand if no labels left
    );
  } catch (error) {
    console.error("Error deleting label:", error);
    toast.error("Failed to delete neck label");
  }
};

  // Open edit modal
  const handleEditClick = (label) => {
    setSelectedLabel(label);
    setShowEditModal(true);
  };

  // Handle file selection for edit
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile({
          file: file,
          preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Update neck label with new image
  const handleUpdateLabel = async () => {
    if (!selectedFile || !selectedLabel) {
      toast.error("Please select a file");
      return;
    }

    try {
      // Delete old image from storage
      try {
        const oldImageRef = ref(Storage, selectedLabel.Neck_label);
        await deleteObject(oldImageRef);
      } catch (storageError) {
        console.log("Old image deletion error:", storageError);
      }

      // Upload new image
      const storageRef = ref(
        Storage,
        `neck_labels/${selectedLabel.userId}/${selectedLabel.isDark ? 'dark' : 'light'}_${Date.now()}_${selectedFile.file.name}`
      );

      const uploadTask = uploadBytesResumable(storageRef, selectedFile.file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          toast.error("Failed to upload new image");
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Update Firestore document
          const docRef = doc(db, 'users', selectedLabel.userId, 'branding', selectedLabel.id);
          await updateDoc(docRef, {
            Neck_label: downloadURL,
            timestamp: new Date()
          });

          toast.success("Neck label updated successfully!");
          setShowEditModal(false);
          setSelectedLabel(null);
          setSelectedFile(null);
          setUploadProgress(0);

          // Refresh the list
          fetchBrandsWithNeckLabels();
        }
      );
    } catch (error) {
      console.error("Error updating label:", error);
      toast.error("Failed to update neck label");
    }
  };

  
// Format date
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',  // Added seconds
    hour12: true
  });
};

  return (
    <div className="flex h-screen bg-gray-50">
 
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
      
        <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
      
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin - Neck Labels Management</h1>
              <p className="text-gray-600">Manage all brand neck labels from one place</p>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
              </div>
            )}

            {/* No Data State */}
            {!loading && brandsWithNeckLabels.length === 0 && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
                <p className="text-gray-500 text-lg">No brands with neck labels found</p>
              </div>
            )}

            {/* Brands List */}
            {!loading && brandsWithNeckLabels.length > 0 && (
              <div className="space-y-6">
                {brandsWithNeckLabels.map((brand, index) => (
                  <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                    {/* Brand Header */}
                    <div className="mb-6 pb-4 border-b border-gray-200">
                      <h2 className="text-xl font-bold text-gray-900">{brand.brandName}</h2>
                      <p className="text-sm text-gray-600">{brand.email}</p>
                      <p className="text-xs text-gray-500 mt-1">User ID: {brand.userId}</p>
                    </div>

                    {/* Neck Labels Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {brand.neckLabels.map((label) => (
                        <div
                          key={label.id}
                          className="border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow"
                        >
                          {/* Image */}
                          <div className="aspect-square w-full mb-4 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={label.Neck_label}
                              alt={`${label.isDark ? 'Dark' : 'Light'} Neck Label`}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Label Info */}
                          <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between">
  <span className="text-sm font-semibold text-gray-700">Type:</span>
  <span
    className={`px-3 py-1 rounded-full text-xs font-medium ${
      label.isDark === true
        ? 'bg-gray-800 text-white'
        : label.isDark === false
        ? 'bg-gray-100 text-gray-800'
        : 'bg-yellow-100 text-yellow-800'
    }`}
  >
    {label.isDark === true 
      ? 'Dark Garment' 
      : label.isDark === false 
      ? 'Light Garment' 
      : 'N/A'}
  </span>
</div>
                            <div>
                              <span className="text-sm font-semibold text-gray-700">Uploaded:</span>
                              <p className="text-xs text-gray-600">{formatDate(label.timestamp)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-semibold text-gray-700">Product:</span>
                              <p className="text-xs text-gray-600">{label.productName || 'Neck Label'}</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditClick(label)}
                              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(label)}
                              className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                            >
                               Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">Delete Neck Label</h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to delete this {selectedLabel?.isDark ? 'dark' : 'light'} garment neck label?
                </p>
                <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>

                {/* Preview */}
                {selectedLabel && (
                  <div className="mb-6">
                    <img
                      src={selectedLabel.Neck_label}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-lg mx-auto border-2 border-gray-200"
                    />
                  </div>
                )}

                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedLabel(null);
                    }}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteLabel}
                    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Edit Neck Label</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedLabel(null);
                    setSelectedFile(null);
                    setUploadProgress(0);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                
                </button>
              </div>

              {/* Current Image */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">Current Image:</p>
                <img
                  src={selectedLabel?.Neck_label}
                  alt="Current"
                  className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                />
                <p className="text-xs text-gray-600 mt-2">
                  Type: {selectedLabel?.isDark ? 'Dark Garment' : 'Light Garment'}
                </p>
              </div>

              {/* New Image Upload */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">Upload New Image:</p>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-black transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="edit-file-input"
                  />
                  <label htmlFor="edit-file-input" className="cursor-pointer">
                    {selectedFile ? (
                      <img
                        src={selectedFile.preview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="py-8">
                        <svg
                          className="w-12 h-12 mx-auto text-gray-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="text-sm text-gray-600">Click to select new image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress > 0 && (
                <div className="mb-6">
                  <div className="rounded-full h-2 overflow-hidden bg-gray-200">
                    <div
                      className="h-full bg-black transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-center mt-1 text-gray-600">
                    Uploading: {Math.round(uploadProgress)}%
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedLabel(null);
                    setSelectedFile(null);
                    setUploadProgress(0);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLabel}
                  disabled={!selectedFile || uploadProgress > 0}
                  className="flex-1 px-6 py-3 bg-black hover:bg-gray-800 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadProgress > 0 ? 'Uploading...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AdminNeckLabels;